import { Link, useSearchParams } from 'react-router'
import { lazy, Suspense, useEffect, useState } from 'react'
import { getPublicCharacters, type Character } from '@/shared/lib/characters'
import { useAuth } from '@/shared/hooks/useAuth'
import { useSEO } from '@/shared/hooks/useSEO'
import { UserMenu } from '@/shared/components/UserMenu'
import { useLocaleStore } from '@/shared/lib/i18n'
import { useScrollReveal } from './useScrollReveal'
import { WorldHomeSections } from './WorldHomeSections'

let callUiPreloadPromise: Promise<typeof import('@/features/call-image/CallImagePage')> | null = null

function prefetchCallUI() {
  if (!callUiPreloadPromise) callUiPreloadPromise = import('@/features/call-image/CallImagePage')
  return callUiPreloadPromise
}

const CallUI = lazy(() => prefetchCallUI().then((m) => ({ default: m.CallImage }))) as React.LazyExoticComponent<React.ComponentType<{ charId?: string; guestUserId?: string; onExit?: () => void; presentation?: 'page' | 'home-overlay' }>>

const allChars = getPublicCharacters()

export function WorldHome() {
  const { isAuthenticated, isLoading } = useAuth()
  const { locale } = useLocaleStore()
  const [searchParams] = useSearchParams()
  const heroRef = useScrollReveal<HTMLDivElement>()
  const [guestId] = useState(() => `vis-${crypto.randomUUID()}`)
  const [isCallOpen, setIsCallOpen] = useState(() => searchParams.get('call') === '1')
  const requestedLang = searchParams.get('lang')

  const chars = allChars.filter(c => c.fixedLanguage === locale)
  const hasChars = chars.length > 0
  const startCharId = locale === 'ja' ? 'mira-jp' : 'mira'

  function openMiraCall() {
    void prefetchCallUI()
    setIsCallOpen(true)
  }

  function closeMiraCall() {
    setIsCallOpen(false)
    if (typeof window !== 'undefined') {
      const next = new URL(window.location.href)
      next.searchParams.delete('call')
      next.searchParams.delete('guestUserId')
      window.history.replaceState({}, '', `${next.pathname}${next.search}${next.hash}`)
    }
  }

  function jumpToSurfaces() {
    document.getElementById('persons')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  const copy = locale === 'ja'
    ? {
        seoTitle: 'Leka AI | 人を動かす follow-through engine',
        seoDesc: 'Leka は、今日の一歩を具体化し、proof を置き、未達なら最小の再開まで運ぶ follow-through engine。仕事、勉強、内面、体、お金を one engine で支える。',
        eyebrow: 'ONE ENGINE, FIVE SURFACES',
        headline: '止まった日でも、ここから動かす',
        sub: 'トップからそのまま話せる。\nMira が最初の一歩を決め、必要なら正しい surface に渡す。',
        gridTitle: 'SURFACES',
        explainerTitle: '1 engine, 5 public surfaces',
        explainerBody: 'これは別々の AI friend のカタログじゃない。1つの follow-through engine を、仕事、勉強、内面、体、お金向けに包み直している。共通の rhythm は、今日の一歩、proof、recovery、夜の回収。',
        primaryCta: '今すぐ始める',
        secondaryCta: 'surface を見る',
      }
    : {
        seoTitle: 'Leka AI | One engine for follow-through',
        seoDesc: 'Leka gets humans into motion by making today\'s step concrete, defining proof, recovering fast after misses, and closing the day cleanly across work, study, inner life, body, and money.',
        eyebrow: 'ONE ENGINE, FIVE SURFACES',
        headline: 'Get into motion, not just insight',
        sub: 'Start from the homepage.\nMira finds the first honest step, then routes you into the right surface when needed.',
        gridTitle: 'SURFACES',
        explainerTitle: 'One engine, five public surfaces',
        explainerBody: 'This is not a catalog of separate AI friends. It is one follow-through engine, wrapped for work, study, inner life, body, and money. The rhythm stays the same: today\'s step, proof, recovery, and a clean close.',
        primaryCta: 'Start now',
        secondaryCta: 'See surfaces',
      }

  useSEO({
    title: copy.seoTitle,
    description: copy.seoDesc,
    canonical: 'https://tryleka.com',
    ogImage: 'https://tryleka.com/banner-preview.jpg',
    ogImageAlt: 'Leka AI — One engine for follow-through',
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: 'Leka AI',
      alternateName: ['Leka', 'レカAI'],
      url: 'https://tryleka.com',
      description: copy.seoDesc,
      publisher: { '@type': 'Organization', name: 'Leka AI', url: 'https://tryleka.com', sameAs: ['https://x.com/lekaai'] },
    },
  })

  useEffect(() => {
    if ((requestedLang === 'en' || requestedLang === 'ja') && requestedLang !== locale) {
      useLocaleStore.getState().setLocale(requestedLang)
    }
    if (searchParams.get('call') === '1') setIsCallOpen(true)
  }, [locale, requestedLang, searchParams])

  useEffect(() => {
    const timer = window.setTimeout(() => { void prefetchCallUI() }, 700)
    return () => window.clearTimeout(timer)
  }, [])

  useEffect(() => {
    if (!isCallOpen) return
    const prevOverflow = document.body.style.overflow
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeMiraCall()
    }
    document.body.style.overflow = 'hidden'
    window.addEventListener('keydown', onKeyDown)
    return () => {
      document.body.style.overflow = prevOverflow
      window.removeEventListener('keydown', onKeyDown)
    }
  }, [isCallOpen])

  return (
    <>
      {isCallOpen && (
        <>
          <div
            className="fixed inset-0 z-[110] pointer-events-none"
            style={{
              background: 'radial-gradient(circle at top, rgba(212,186,112,0.16), transparent 42%), rgba(5,5,5,0.34)',
              backdropFilter: 'blur(14px)',
              WebkitBackdropFilter: 'blur(14px)',
            }}
          />
          <Suspense fallback={
            <div className="fixed inset-0 z-[120] flex items-center justify-center">
              <div className="w-8 h-8 border-2 border-white/20 border-t-white rounded-full animate-spin" />
            </div>
          }>
            <div
              className="fixed inset-0 z-[120] overflow-hidden"
              style={{ animation: 'callLayerIn 260ms cubic-bezier(0.22, 1, 0.36, 1) both' }}
            >
              <button
                onClick={closeMiraCall}
                aria-label="Close"
                style={{
                  position: 'fixed',
                  top: 16,
                  right: 16,
                  zIndex: 140,
                  minWidth: 74,
                  height: 38,
                  padding: '0 14px',
                  borderRadius: '999px',
                  border: '1px solid rgba(255,255,255,0.14)',
                  background: 'rgba(12,12,12,0.58)',
                  color: 'rgba(255,255,255,0.86)',
                  fontSize: '12px',
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  backdropFilter: 'blur(14px)',
                  WebkitBackdropFilter: 'blur(14px)',
                  cursor: 'pointer',
                  boxShadow: '0 10px 30px rgba(0,0,0,0.24)',
                }}
              >
                Close
              </button>
              <CallUI
                charId={startCharId}
                guestUserId={isAuthenticated ? undefined : guestId}
                onExit={closeMiraCall}
                presentation="home-overlay"
              />
            </div>
          </Suspense>
        </>
      )}

      <WorldHomeSections
        copy={copy}
        chars={chars}
        hasChars={hasChars}
        heroRef={heroRef}
        locale={locale}
        isCallOpen={isCallOpen}
        isAuthenticated={isAuthenticated}
        isLoading={isLoading}
        onOpenMiraCall={openMiraCall}
        onJumpToSurfaces={jumpToSurfaces}
      />
    </>
  )
}

export type { Character }
